// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */

import React from "react";
import { ProtocolData } from "../../../../apis/types/protocolTypes";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import ResultsTable from "../../../Lumber/Search/ResultsTable";
import { AnyFilter } from "../../../Lumber/Search/types";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useUserSettings } from "../../../../hooks/useUserSettings";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import ProtocolFavoritingWidget from "../../Widgets/Favorites/ProtocolFavoritingWidget";

interface Props {
	loading: boolean;
	data: ProtocolData[];
	filters: { [key in keyof ProtocolData]?: AnyFilter };
	searchTerm: { term: string; cols: (keyof ProtocolData)[] };
}

const ProtocolSearchResults = ({ data, loading, filters, searchTerm }: Props) => {
	const moduleNavigation = useModuleNavigation();
	const userSettings = useUserSettings();
	const userAuth = useUserAuth();

	const tableHeaders = {
		protocolName: "Protocol",
		protocolTrials: "Trials",
		clientName: "Client",
		geographyName: "Location",
		cropName: "Crop",
		growingSeasonName: "Growing Season",
		owner: "Owner",
		protocolVariables: "Variables Tested",
		statuses: "Status",
	};

	return (
		<ResultsTable<ProtocolData>
			level={"protocol"}
			rowKey={"id"}
			loading={loading}
			data={data}
			headers={tableHeaders}
			columnFilters={filters}
			searchTerm={searchTerm}
			onRenderDataColumn={{
				protocolTrials: (pts) => pts?.map((pt) => pt.trialName) ?? undefined,
				protocolVariables: (vs) =>
					vs?.reduce<string[]>((acc, v) => {
						if (v.checked) {
							acc.push(v.name);
						}
						return acc;
					}, []) ?? undefined,
				statuses: (s) => [
					{
						icon: s.hasExclusions ? "warning solid" : "warning",
						color: "red",
						title: `Protocol Has Trials With ${s.hasExclusions ? "Full Plot" : "Partial"} Exclusions`,
						enabled: s.hasExclusions || s.hasPartialExclusions,
					},
					{
						icon: s.hasFlaggings ? "flag solid" : "flag",
						color: "yellow",
						title: `Protocol Has Trials With ${s.hasFlaggings ? "Full Plot" : "Partial"} Flaggings`,
						enabled: s.hasFlaggings || s.hasPartialFlaggings,
					},
				],
			}}
			onSortColumn={{
				protocolName: true,
				clientName: true,
				geographyName: true,
				cropName: true,
				growingSeasonName: true,
				owner: true,
			}}
			links={{
				protocolName: (row) => moduleNavigation.createProtocolLink(true, row.id, "overview-status"),
				protocolTrials: (pt) => moduleNavigation.createTrialLink(false, pt.trialId, "analysis"),
			}}
			actions={[
				{
					hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.ProtocolEdit, row.clientId),
					icon: "pencil",
					title: "Edit",
					to: (row) => moduleNavigation.createProtocolLink(false, row.id, "info"),
				},
				{
					hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.ProtocolAnalysis, row.clientId),
					icon: "chart bar outline",
					title: "Analysis",
					to: (row) => moduleNavigation.createProtocolLink(false, row.id, "analysis"),
				},
				{
					hasArea: () => true,
					component: (row) => (
						<ProtocolFavoritingWidget
							key={row.id}
							clientId={row.clientId}
							protocolId={row.id}
							userSettings={userSettings}
							displayMode="table"
						/>
					),
				},
			]}
		/>
	);
};

export default ProtocolSearchResults;
